import * as qs from 'qs';
import { ApiManager, VITE_API_URL, VITE_BASE_URL } from '../ApiManager/ApiManager.tsx';
import type { AxiosRequestConfig } from 'axios';
import { IHealthcareProfessionalPayload } from '../../entities/healthcareProfessional/HealthcareProfessional.ts';
import { ChangePasswordPostModel, LoginPostModel, SetLanguagePostModel } from '../LoginApi/LoginApi.ts';
import { IHealthcareFacilityDefaultTaskInput, IHealthcareFacilityPayload } from '../../entities/healthcareFacility/HealthcareFacility.ts';
import { IPatientsInitialProfilePayload, IPatientsRegisterPayload } from '../../entities/Patients/Patient.ts';
import { IPatientInvitations } from '../../entities/PatientInvitations/PatientInvitations.ts';
import { ILifeEvent } from '../../entities/lifeEvents/LifeEvent.ts';
import { IQuestionnairesPayload } from '../../entities/Questionnaires/Questionnaires.ts';
import { ICollaboratorPayload, IPartialCollaboratorPayload } from '../../entities/Collaborator/Collaborator.ts';
import { ITestKitOrderPayload } from '../../entities/TestKitOrder/TestKitOrder.ts';
import { ITaskPayload } from '../../entities/Tasks/Tasks.ts';
import { ISimulationPayload } from '../../entities/Simulation/Simulation.ts';
import { IPrescription } from '../../entities/Prescription/Prescription.ts';
import { IQuestionnaireAnswerPayload } from '../../entities/QuestionnaireAnswer/QuestionnaireAnswer.ts';
import { IPhysiologicalData, IPhysiologicalDataDetails } from '../../entities/PhysiologicalData/PhysiologicalData.ts';
import { IMedicalNote } from '../../entities/MedicalNote/MedicalNote.ts';
import { IDocument } from '../../entities/Document/Document.ts';
import { ICreateClinicSubscription } from '../../entities/clinicSubscription/ClinicSubscription.ts';
import { ICreateDelegationModel } from '../HcpDelegationApi/HcpDelegationApi.ts';
import { IBatchDoseSuggestionRequest, IMMECalculationRequest } from '../../entities/MME/MME.ts';
import { ISupportRequestPayload } from '../SupportRequestApi/SupportRequestApi.ts';
import { IRegistrationFieldsConfigInput } from '../../entities/RegistrationFields/RegistrationFields.ts';
import { IRecordLegalAcceptance } from '../../entities/Legal/Legal.ts';
import { ICarePlanTemplateInput } from '../../entities/CarePlanTemplate/CarePlanTemplate.ts';

export enum HttpMethodEnum {
    GET,
    POST,
    PUT,
    DELETE,
    HEAD,
    PATCH,
}

export enum DataType {
    Json,
    QueryString,
}

interface ApiRequest {
    method: HttpMethodEnum;
    path: string;
    data?:
        | string
        | {
              token: string;
          }
        | {
              completionDate: Date;
          }
        | IQuestionnaireAnswerPayload
        | IPrescription[]
        | ISimulationPayload
        | IPartialCollaboratorPayload
        | ITaskPayload
        | ITestKitOrderPayload
        | ICollaboratorPayload
        | IQuestionnairesPayload
        | ILifeEvent
        | SetLanguagePostModel
        | IHealthcareFacilityPayload
        | IHealthcareProfessionalPayload
        | LoginPostModel
        | ChangePasswordPostModel
        | IPatientInvitations
        | IMedicalNote
        | IPatientsInitialProfilePayload
        | IPatientsRegisterPayload
        | IPhysiologicalData
        | IPhysiologicalDataDetails
        | IDocument
        | ICreateClinicSubscription
        | ICreateDelegationModel
        | IHealthcareFacilityDefaultTaskInput[]
        | IMMECalculationRequest
        | IBatchDoseSuggestionRequest
        | ISupportRequestPayload
        | IRegistrationFieldsConfigInput
        | IRecordLegalAcceptance
        | ICarePlanTemplateInput
        | { reason: string }
        | { status: string }
        | { eventDate?: string }
        | { password: string };
    id?: string;
    dataType?: DataType;
    extraOptionsConfig?: AxiosRequestConfig;
    suppressGlobalErrorHandling?: boolean;
}

export class BaseApi {
    static get baseUrl(): string {
        if (!VITE_BASE_URL) 
            console.error('No base url specified');

        return VITE_BASE_URL ?? '';
    }

    protected static get apiUrl(): string {
        if (!VITE_API_URL) 
            console.error('No api url specified');

        return VITE_API_URL ?? '';
    }

    protected static sendApiRequest<TResult = void>({
        method,
        path,
        data,
        id,
        dataType,
        extraOptionsConfig,
        suppressGlobalErrorHandling,
    }: ApiRequest): Promise<TResult> {
        if (id) path = path.replace(':id', id);

        let axiosOptions: AxiosRequestConfig = {};

        const sendDataAsQueryString =
            dataType === DataType.QueryString ||
            [HttpMethodEnum.GET, HttpMethodEnum.DELETE, HttpMethodEnum.HEAD].some((x) => x === method);

        if (sendDataAsQueryString) {
            axiosOptions = {
                ...this.buildQueryStringOptions(data),
            };
        }

        axiosOptions = {
            ...axiosOptions,
            ...extraOptionsConfig,
        };

        // Add custom header to suppress global error handling if requested
        if (suppressGlobalErrorHandling) {
            axiosOptions.headers = {
                ...axiosOptions.headers,
                'X-Suppress-Global-Error-Handling': 'true',
            };
        }

        switch (method) {
            case HttpMethodEnum.GET:
                return ApiManager.get<TResult>(path, axiosOptions).then((result) => result.data);
            case HttpMethodEnum.POST: {
                let payload = !sendDataAsQueryString ? data : null;
                const options = { ...axiosOptions };

                if (typeof payload === 'string') {
                    payload = JSON.stringify(payload); // ← envoie la string encodée en JSON
                    options.headers = {
                        ...options.headers,
                        'Content-Type': 'application/json',
                    };
                }

                return ApiManager.post<TResult>(path, payload, options).then((result) => {
                    return result.data;
                });
            }
            case HttpMethodEnum.PUT:
                return ApiManager.put<TResult>(path, !sendDataAsQueryString ? data : null, {
                    ...axiosOptions,
                    headers: {
                        ...axiosOptions?.headers,
                        'Cache-Control': 'no-Store', // Prevent caching
                        Pragma: 'no-cache', // Legacy support for older browsers
                    },
                    withCredentials: true,
                }).then((result) => result.data);
            case HttpMethodEnum.DELETE:
                return ApiManager.delete<TResult>(path, axiosOptions).then((result) => result.data);
            case HttpMethodEnum.HEAD:
                return ApiManager.head<TResult>(path, axiosOptions).then((result) => result.data);
            case HttpMethodEnum.PATCH:
                return ApiManager.patch<TResult>(path, !sendDataAsQueryString ? data : null, axiosOptions).then(
                    (result) => result.data,
                );
        }
    }

    private static buildQueryStringOptions<T>(data: T): AxiosRequestConfig {
        return {
            paramsSerializer: (params) => qs.stringify(params),
            params: data,
        };
    }

    protected static sendApiRequestWithSuppressedErrors<TResult = void>(apiRequest: Omit<ApiRequest, 'suppressGlobalErrorHandling'>): Promise<TResult> {
        return this.sendApiRequest<TResult>({
            ...apiRequest,
            suppressGlobalErrorHandling: true
        });
    }
}
